import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, Pencil } from 'lucide-react';
import { PaymentMethodModal } from './PaymentMethodModal';
import { CardBrandIcon } from './CardBrandIcon';
import { toast } from 'sonner';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { useCustomAuth } from '@/contexts/CustomAuthContext';

interface PaymentMethod {
  id: string;
  id_stripe: string;
  type: string;
  last4: string;
  holder: string;
  expiry_month: number;
  expiry_year: number;
  is_default: boolean;
  brand: string;
}

interface PaymentMethodsCardProps {
  paymentMethods?: PaymentMethod[];
  isLoading?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
}

export const PaymentMethodsCard: React.FC<PaymentMethodsCardProps> = ({
  paymentMethods: propPaymentMethods,
  isLoading: propIsLoading,
  containerRef
}) => {
  useLanguage();
  const { user } = useCustomAuth();
  // Sempre pula o fetch automático — dados vêm do getDashboard da Finance page.
  // Após mutations (add/delete), fetchCards() popula hookResult.paymentMethods e tem prioridade.
  const hookResult = usePaymentMethods(user?.id_Stripe_Client, true);

  const paymentMethods = hookResult.paymentMethods.length > 0
    ? hookResult.paymentMethods
    : (propPaymentMethods ?? []);
  const isLoading = propIsLoading ?? hookResult.isLoading;
  const selectedCard = hookResult.selectedCard;
  const fetchCards = hookResult.fetchCards;
  const setDefaultCard = hookResult.setDefaultCard;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit' | 'select'>('add');

  // Sempre prioriza o cartão marcado como default no backend; selectedCard é fallback de UI
  const defaultCard = paymentMethods?.find(card => card.is_default) || selectedCard || paymentMethods?.[0];

  const handleOpenAddModal = () => {
    setModalMode('add');
    setModalOpen(true);
  };

  const handleOpenSelectModal = () => {
    setModalMode('select');
    setModalOpen(true);
  };

  const handleSave = (data: any) => {
    if (modalMode === 'add') {
      toast.success(translateInterfaceText("Cartão adicionado com sucesso!"));
    } else {
      toast.success(translateInterfaceText("Cartão atualizado com sucesso!"));
    }
    console.log('Payment method data:', data);
    fetchCards(); // Recarrega os cartões
  };

  const editInitialData = defaultCard ? {
    cardNumber: `**** **** **** ${defaultCard.last4}`,
    cardHolder: defaultCard.holder || 'Titular do cartão',
    expiry: `${String(defaultCard.expiry_month).padStart(2, '0')}/${String(defaultCard.expiry_year).slice(-2)}`,
    cvc: '•••',
    isDefault: defaultCard.is_default,
  } : {
    cardNumber: '**** **** **** 0000',
    cardHolder: 'Sem cartão cadastrado',
    expiry: '00/00',
    cvc: '•••',
    isDefault: false,
  };

  return (
    <>
      <Card 
        className="border border-[#E5E7EB] rounded-[14px] overflow-hidden"
        style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}
      >
        <div className="p-4">
          {/* Header */}
          <div className="mb-3">
            <h2 className="text-[18px] font-semibold text-[#111827]">{translateInterfaceText("Formas de Pagamento")}</h2>
            <p className="text-[13px] text-[#6B7280] mt-0.5">{translateInterfaceText("Gerencie seus cartões e métodos de pagamento")}</p>
          </div>
          
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-3 bg-gray-50 rounded-[12px] animate-pulse">
                <div className="h-12 bg-gray-200 rounded"></div>
              </div>
            ) : !defaultCard ? (
              <div className="p-4 text-center text-gray-500">
                <p className="text-sm">{translateInterfaceText("Nenhum cartão cadastrado")}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={handleOpenAddModal}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {translateInterfaceText("Adicionar cartão")}</Button>
              </div>
            ) : (
              /* Card do cartão - Premium style */
              <div
                className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 p-3 bg-white border border-[#E5E7EB] rounded-[12px]"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}
              >
                <div className="flex items-center gap-2.5">
                  {/* Card brand icon */}
                  <CardBrandIcon brand={defaultCard.brand || 'unknown'} className="w-10 h-7" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-[13px] font-medium text-[#111827]">
                        {defaultCard.brand || translateInterfaceText("Cartão")} {" "}{translateInterfaceText("terminando em")}{" "}<span className="font-semibold">{defaultCard.last4}</span>
                      </p>
                      {defaultCard.is_default && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-[6px] bg-[#2563EB]/10 text-[#2563EB]">
                          {translateInterfaceText("Principal")}</span>
                      )}
                    </div>
                    <p className="text-[11px] text-[#6B7280]">
                      {translateInterfaceText("Expira em")}{" "}{String(defaultCard.expiry_month).padStart(2, '0')}/{String(defaultCard.expiry_year).slice(-2)}
                    </p>
                  </div>
                </div>
              
                {/* Botões na mesma linha */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2.5 border-[#D1D5DB] text-[#374151] rounded-[8px] gap-1 hover:bg-[#2563EB] hover:text-white hover:border-[#2563EB] transition-colors text-[12px]"
                    onClick={handleOpenSelectModal}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    {translateInterfaceText("Trocar")}</Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2.5 border-[#2563EB] text-[#2563EB] rounded-[8px] gap-1 hover:bg-[#2563EB] hover:text-white transition-colors text-[12px]"
                    onClick={handleOpenAddModal}
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    {translateInterfaceText("+ Adicionar")}</Button>
                </div>
              </div>
            )}
          </CardContent>
        </div>
      </Card>

      <PaymentMethodModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        mode={modalMode}
        existingCards={paymentMethods}
        selectedCard={selectedCard}
        onCardSelect={async (card) => {
          if (card.is_default) {
            return;
          }
          await setDefaultCard(card.id_stripe);
        }}
        onCardDelete={async (cardId) => {
          await hookResult.deleteCard(cardId);
        }}
        onRefreshCards={async () => {
          await fetchCards();
        }}
        onSave={handleSave}
      />
    </>
  );
};